/**
 * BL-182 Gate 8 — Sub-account billing rollup.
 *
 * Wires the already-real sub_accounts table (parent_account_id, term_overrides,
 * rollup_to_parent) into consolidated parent billing.
 *
 * Join path: invoice.entity_id → sub_account.entity_id → sub_account.parent_account_id
 *
 * term_overrides (JSONB on sub_account) can carry:
 *   { discount_pct: number }   — additional percentage discount applied to the
 *                                sub-account's invoice totals in the rollup.
 *   Any other keys are preserved but not applied by this layer (reserved for
 *   future gates such as override payment terms, cycle, etc.).
 *
 * All amounts are in dollars. Callers are responsible for ensuring the invoices
 * and subAccounts arrays are already org-scoped (RLS handles this server-side).
 */

import { getInvoiceDisplayAmount } from './invoiceHelpers';

const roundMoney = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Apply a sub-account's term_overrides to a raw invoice amount.
 *
 * Supported overrides:
 *   discount_pct {number} — percentage discount (0–100) off the invoice total.
 *
 * @param {number} baseAmount
 * @param {object} termOverrides — the term_overrides JSONB value from sub_accounts
 * @returns {{ adjustedAmount: number, discountApplied: number }}
 */
export function applyTermOverrides(baseAmount, termOverrides) {
    const base = Number(baseAmount) || 0;
    const overrides = termOverrides && typeof termOverrides === 'object' ? termOverrides : {};

    const discountPct = Math.min(100, Math.max(0, Number(overrides.discount_pct) || 0));
    const discountApplied = roundMoney((base * discountPct) / 100);
    const adjustedAmount = roundMoney(base - discountApplied);

    return { adjustedAmount, discountApplied };
}

/**
 * Group a flat array of invoices into parent-account rollup groups.
 *
 * Only invoices that match a sub-account via entity_id AND whose sub-account
 * has rollup_to_parent === true are included. Invoices with no matching
 * sub-account pass through as "standalone" (not part of any rollup).
 *
 * @param {object[]} invoices     — invoice rows (must include entity_id)
 * @param {object[]} subAccounts  — sub_account rows (must include entity_id,
 *                                   parent_account_id, term_overrides,
 *                                   rollup_to_parent)
 * @returns {{
 *   rollupGroups: Object.<string, {
 *     parent_account_id: string,
 *     entries: Array<{
 *       sub_account_id: string,
 *       sub_account_name: string,
 *       invoice: object,
 *       rawAmount: number,
 *       adjustedAmount: number,
 *       discountApplied: number,
 *       termOverrides: object
 *     }>,
 *     rollupTotal: number,
 *     totalDiscount: number
 *   }>,
 *   standaloneInvoices: object[]
 * }}
 */
export function groupInvoicesForParentRollup(invoices, subAccounts) {
    if (!Array.isArray(invoices)) return { rollupGroups: {}, standaloneInvoices: [] };
    if (!Array.isArray(subAccounts) || subAccounts.length === 0) {
        return { rollupGroups: {}, standaloneInvoices: invoices.slice() };
    }

    // Build entity_id → sub_account map (only rollup-enabled sub-accounts).
    const entityToSubAccount = new Map();
    for (const sa of subAccounts) {
        if (sa.rollup_to_parent === false) continue; // explicitly opted out
        if (!sa.entity_id) continue;
        entityToSubAccount.set(sa.entity_id, sa);
    }

    const rollupGroups = {};
    const standaloneInvoices = [];

    for (const invoice of invoices) {
        const sa = invoice.entity_id ? entityToSubAccount.get(invoice.entity_id) : undefined;

        if (!sa) {
            standaloneInvoices.push(invoice);
            continue;
        }

        const parentId = sa.parent_account_id;
        const rawAmount = getInvoiceDisplayAmount(invoice);
        const { adjustedAmount, discountApplied } = applyTermOverrides(
            rawAmount,
            sa.term_overrides ?? {}
        );

        if (!rollupGroups[parentId]) {
            rollupGroups[parentId] = {
                parent_account_id: parentId,
                entries: [],
                rollupTotal: 0,
                totalDiscount: 0,
            };
        }

        rollupGroups[parentId].entries.push({
            sub_account_id:   sa.id,
            sub_account_name: sa.sub_account_name,
            invoice,
            rawAmount,
            adjustedAmount,
            discountApplied,
            termOverrides: sa.term_overrides ?? {},
        });

        rollupGroups[parentId].rollupTotal   = roundMoney(rollupGroups[parentId].rollupTotal   + adjustedAmount);
        rollupGroups[parentId].totalDiscount = roundMoney(rollupGroups[parentId].totalDiscount + discountApplied);
    }

    return { rollupGroups, standaloneInvoices };
}

/**
 * Build a STMT invoice payload that consolidates all sub-account charges
 * under a single parent account. The payload is ready to INSERT into the
 * `invoices` table.
 *
 * The individual sub-account INV rows are not deleted — the STMT carries
 * their combined adjusted total and a reference list in `notes` for
 * roundtrip traceability.
 *
 * @param {object} params
 * @param {string}   params.parentAccountId
 * @param {object[]} params.entries          — rollupGroups[id].entries
 * @param {string}   params.orgId
 * @param {string}   [params.statementDate]  — ISO date string, defaults to today
 * @returns {object}  — ready to INSERT into `invoices`
 */
export function buildParentRollupStatementPayload({
    parentAccountId,
    entries,
    orgId,
    statementDate,
}) {
    const today = statementDate || new Date().toISOString().split('T')[0];
    const total = roundMoney(entries.reduce((sum, e) => sum + e.adjustedAmount, 0));
    const sourceIds = entries.map((e) => e.invoice.id).filter(Boolean);

    return {
        org_id:        orgId,
        account_id:    parentAccountId,
        document_type: 'STMT',
        invoice_date:  today,
        status:        'outstanding',
        total,
        final_amount:  total,
        // notes carries the source invoice IDs and per-sub-account summary
        // for roundtrip traceability — matches the Gate 3 consolidation pattern.
        notes: JSON.stringify({
            consolidated_invoice_ids: sourceIds,
            sub_account_rollup: entries.map((e) => ({
                sub_account_id:   e.sub_account_id,
                sub_account_name: e.sub_account_name,
                invoice_id:       e.invoice.id,
                raw_amount:       e.rawAmount,
                adjusted_amount:  e.adjustedAmount,
                discount_applied: e.discountApplied,
            })),
        }),
    };
}

/**
 * Retrieve the sub-account rollup summary embedded in a STMT invoice's notes.
 *
 * @param {string|null} notes
 * @returns {Array<{sub_account_id, sub_account_name, invoice_id, raw_amount, adjusted_amount, discount_applied}>}
 */
export function parseRollupNotes(notes) {
    if (!notes) return [];
    try {
        const parsed = JSON.parse(notes);
        return Array.isArray(parsed.sub_account_rollup) ? parsed.sub_account_rollup : [];
    } catch {
        return [];
    }
}
